import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc as firestoreDoc, onSnapshot, getDoc as getDocFromFirestore, collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Loader2, Trophy, Users, Clock, Pause, Play, Flag, Maximize, Minimize, Award, Target, Zap, Crown, Shield, ArrowLeft, Flame } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FanViewPage = () => {
  const { matchId } = useParams(); // This is actually fixtureId
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [fixture, setFixture] = useState(null);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Debug: Log when teams state changes
  useEffect(() => {
    console.log('Teams state updated:', teams);
  }, [teams]);

  useEffect(() => {
    if (!matchId) {
      setError('No match ID provided');
      setLoading(false);
      return;
    }

    let fixtureUnsubscribe = null;

    const setupListeners = async () => {
      // Listen to fixture updates (this is where the live data is stored)
      fixtureUnsubscribe = onSnapshot(firestoreDoc(db, 'fixtures', matchId), async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const fixtureData = { id: docSnapshot.id, ...docSnapshot.data() };
          console.log('Fixture data received at:', new Date().toLocaleTimeString(), fixtureData);
          console.log('Fixture homeTeamId:', fixtureData.homeTeamId);
          console.log('Fixture awayTeamId:', fixtureData.awayTeamId);
          setFixture(fixtureData);
          
          // Fetch team data for logos
          console.log('Fixture data:', fixtureData);
          console.log('Home team ID:', fixtureData.homeTeamId);
          console.log('Away team ID:', fixtureData.awayTeamId);
          console.log('Home team name:', fixtureData.homeTeam);
          console.log('Away team name:', fixtureData.awayTeam);
          
          if (fixtureData.homeTeamId && fixtureData.awayTeamId) {
            try {
              console.log('Fetching team data for:', fixtureData.homeTeamId, fixtureData.awayTeamId);
              
              // Fetch teams separately to avoid minification issues
              const homeTeamRef = firestoreDoc(db, 'teams', fixtureData.homeTeamId);
              const awayTeamRef = firestoreDoc(db, 'teams', fixtureData.awayTeamId);
              
              const homeTeamDoc = await getDocFromFirestore(homeTeamRef);
              const awayTeamDoc = await getDocFromFirestore(awayTeamRef);
              
              const teamData = {};
              if (homeTeamDoc.exists()) {
                const homeTeamData = { id: homeTeamDoc.id, ...homeTeamDoc.data() };
                teamData[fixtureData.homeTeamId] = homeTeamData;
                console.log('Home team data loaded:', homeTeamData);
                console.log('Home team logo URL:', homeTeamData.logo);
              } else {
                console.log('Home team document not found for ID:', fixtureData.homeTeamId);
              }
              if (awayTeamDoc.exists()) {
                const awayTeamData = { id: awayTeamDoc.id, ...awayTeamDoc.data() };
                teamData[fixtureData.awayTeamId] = awayTeamData;
                console.log('Away team data loaded:', awayTeamData);
                console.log('Away team logo URL:', awayTeamData.logo);
              } else {
                console.log('Away team document not found for ID:', fixtureData.awayTeamId);
              }
              
              console.log('Setting teams state with:', teamData);
              setTeams(prevTeams => ({ ...prevTeams, ...teamData }));
            } catch (err) {
              console.error('Error fetching team data:', err);
            }
          } else {
            // Fallback: try to get team names from fixture data
            console.log('No team IDs found, using fixture team names');
            const teamData = {};
            if (fixtureData.homeTeam) {
              teamData['home'] = { name: fixtureData.homeTeam, logo: null };
            }
            if (fixtureData.awayTeam) {
              teamData['away'] = { name: fixtureData.awayTeam, logo: null };
            }
            setTeams(prevTeams => ({ ...prevTeams, ...teamData }));
            
            // Try to find teams by name as a fallback
            if (fixtureData.homeTeam || fixtureData.awayTeam) {
              try {
                console.log('Attempting to find teams by name...');
                const teamsQuery = query(collection(db, 'teams'));
                const teamsSnapshot = await getDocs(teamsQuery);
                const allTeams = {};
                teamsSnapshot.docs.forEach(doc => {
                  allTeams[doc.id] = { id: doc.id, ...doc.data() };
                });
                
                // Try to match by name
                const matchedTeams = {};
                if (fixtureData.homeTeam) {
                  const homeTeamMatch = Object.values(allTeams).find(team => 
                    team.name && team.name.toLowerCase() === fixtureData.homeTeam.toLowerCase()
                  );
                  if (homeTeamMatch) {
                    matchedTeams[fixtureData.homeTeamId || 'home'] = homeTeamMatch;
                    console.log('Found home team by name:', homeTeamMatch);
                  }
                }
                if (fixtureData.awayTeam) {
                  const awayTeamMatch = Object.values(allTeams).find(team => 
                    team.name && team.name.toLowerCase() === fixtureData.awayTeam.toLowerCase()
                  );
                  if (awayTeamMatch) {
                    matchedTeams[fixtureData.awayTeamId || 'away'] = awayTeamMatch;
                    console.log('Found away team by name:', awayTeamMatch);
                  }
                }
                
                if (Object.keys(matchedTeams).length > 0) {
                  console.log('Setting matched teams:', matchedTeams);
                  setTeams(prevTeams => ({ ...prevTeams, ...matchedTeams }));
                }
              } catch (err) {
                console.error('Error fetching teams by name:', err);
              }
            }
          }
          
          // If fixture has match data, use it (live, paused, or completed)
          if ((fixtureData.status === 'live' || fixtureData.status === 'paused' || fixtureData.status === 'completed') && fixtureData.sets) {
            console.log('Setting match data from fixture:', fixtureData);
            setMatch(fixtureData);
          }
          setLoading(false);
        } else {
          console.log('Fixture not found for ID:', matchId);
          setError('Match not found');
          setLoading(false);
        }
      });
    };

    setupListeners();

    return () => {
      if (fixtureUnsubscribe) {
        fixtureUnsubscribe();
      }
    };
  }, [matchId]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const getCurrentSet = () => {
    if (!match?.sets) return null;
    return match.sets.find(set => !set.completed) || match.sets[match.sets.length - 1];
  };

  const getSetScore = () => {
    if (!match?.sets) return { home: 0, away: 0 };
    const homeSetsWon = match.sets.filter(s => s.completed && s.homeScore > s.awayScore).length;
    const awaySetsWon = match.sets.filter(s => s.completed && s.awayScore > s.homeScore).length;
    return { home: homeSetsWon, away: awaySetsWon };
  };

  const getGameScore = () => {
    const currentSet = getCurrentSet();
    if (!currentSet) return { home: 0, away: 0 };
    return { home: currentSet.homeGames || 0, away: currentSet.awayGames || 0 };
  };

  // Tennis scoring logic
  const getTennisScore = (points) => {
    if (points === 0) return '0';
    if (points === 1) return '15';
    if (points === 2) return '30';
    if (points === 3) return '40';
    return '40+';
  };

  const isDeuce = (homePoints, awayPoints) => {
    return homePoints >= 3 && awayPoints >= 3 && homePoints === awayPoints;
  };

  const isAdvantage = (homePoints, awayPoints) => {
    return homePoints >= 3 && awayPoints >= 3 && Math.abs(homePoints - awayPoints) === 1;
  };

  const getCurrentGameScore = (homePoints, awayPoints) => {
    // Handle deuce situations (40-40 and beyond)
    if (homePoints >= 3 && awayPoints >= 3) {
      const diff = homePoints - awayPoints;
      if (diff === 0) {
        // Check if this is the second deuce (Golden Point situation)
        if (homePoints >= 4 && awayPoints >= 4) {
          return 'Golden Point (Next Point Wins)';
        }
        return 'Deuce';
      }
      if (diff === 1) return `Advantage ${match?.homeTeam || 'Home'}`;
      if (diff === -1) return `Advantage ${match?.awayTeam || 'Away'}`;
      if (diff >= 2) return `Game ${match?.homeTeam || 'Home'}`;
      if (diff <= -2) return `Game ${match?.awayTeam || 'Away'}`;
    }
    
    // Handle game completion
    if (homePoints >= 4 && awayPoints < 3) return `Game ${match?.homeTeam || 'Home'}`;
    if (awayPoints >= 4 && homePoints < 3) return `Game ${match?.awayTeam || 'Away'}`;
    
    // Handle set point situations
    if (homePoints === 3 && awayPoints < 3) return `${getTennisScore(homePoints)} - ${getTennisScore(awayPoints)} (Set Point)`;
    if (awayPoints === 3 && homePoints < 3) return `${getTennisScore(homePoints)} - ${getTennisScore(awayPoints)} (Set Point)`;
    
    return `${getTennisScore(homePoints)} - ${getTennisScore(awayPoints)}`;
  };

  const getCurrentPointScore = () => {
    const currentSet = getCurrentSet();
    if (!currentSet) return '0 - 0';
    return getCurrentGameScore(currentSet.homePoints || 0, currentSet.awayPoints || 0);
  };

  const isMatchCompleted = () => {
    // Match is only complete when status is explicitly set to 'completed'
    // This ensures all 3 sets are played and only admin can complete the match
    return match?.status === 'completed';
  };

  const getWinner = () => {
    if (!isMatchCompleted()) return null;
    const setScore = getSetScore();
    return setScore.home > setScore.away ? match?.homeTeam : match?.awayTeam;
  };

  const getPlayerStats = () => {
    if (!match?.playerStats) return { home: [], away: [] };
    
    let homeStats = Object.values(match.playerStats.home || {});
    let awayStats = Object.values(match.playerStats.away || {});
    
    // Debug logging
    console.log('Match selectedPlayers:', match?.selectedPlayers);
    console.log('Match playerStats:', match?.playerStats);
    
    // Ensure player names are populated from selectedPlayers if missing
    if (match?.selectedPlayers) {
      homeStats = homeStats.map((player, index) => ({
        ...player,
        name: player.name || match.selectedPlayers.home[`player${index + 1}`]?.name || `Player ${index + 1}`
      }));
      awayStats = awayStats.map((player, index) => ({
        ...player,
        name: player.name || match.selectedPlayers.away[`player${index + 1}`]?.name || `Player ${index + 1}`
      }));
    }
    
    console.log('Final homeStats:', homeStats);
    console.log('Final awayStats:', awayStats);
    
    return { home: homeStats, away: awayStats };
  };

  const getTeamLogo = (teamId) => {
    const team = teams[teamId];
    console.log('Getting logo for teamId:', teamId, 'team data:', team, 'logo:', team?.logo);
    if (team?.logo) {
      return team.logo;
    }
    // Fallback: check if teams object has the logo stored differently
    const teamKeys = Object.keys(teams);
    for (const key of teamKeys) {
      if (teams[key]?.id === teamId && teams[key]?.logo) {
        return teams[key].logo;
      }
    }
    return null;
  };

  const getTeamName = (teamId) => {
    const team = teams[teamId];
    return team?.name || (teamId === match?.homeTeamId ? match?.homeTeam : match?.awayTeam);
  };

  // Fallback team logo display
  const getDisplayLogo = (teamId, teamName) => {
    const logo = getTeamLogo(teamId);
    if (logo) return logo;
    
    // If no logo, show team name initial
    return null;
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#FFFFFF',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ textAlign: 'center' }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Trophy size={64} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
          </motion.div>
          <p style={{ color: '#4B5563', fontSize: '1.25rem', fontWeight: '600' }}>Loading match...</p>
        </motion.div>
      </div>
    );
  }

  if (error && !match) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#FFFFFF',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px' }}>
          <Trophy size={80} style={{ color: '#D1D5DB', margin: '0 auto 1.5rem', display: 'block' }} />
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1F2937' }}>
            {error}
          </h2>
          <p style={{ fontSize: '1.125rem', color: '#6B7280' }}>
            This match may not be available yet.
          </p>
        </div>
      </div>
    );
  }

  const setScore = getSetScore();
  const gameScore = getGameScore();
  const playerStats = getPlayerStats();
  const winner = getWinner();
  
  console.log('Teams state:', teams);
  console.log('Match homeTeamId:', match?.homeTeamId);
  console.log('Match awayTeamId:', match?.awayTeamId);
  console.log('Match homeTeam:', match?.homeTeam);
  console.log('Match awayTeam:', match?.awayTeam);
  
  const homeTeamLogo = getTeamLogo(match?.homeTeamId);
  const awayTeamLogo = getTeamLogo(match?.awayTeamId);
  const homeTeamName = getTeamName(match?.homeTeamId) || match?.homeTeam;
  const awayTeamName = getTeamName(match?.awayTeamId) || match?.awayTeam;
  
  console.log('Home team logo:', homeTeamLogo);
  console.log('Away team logo:', awayTeamLogo);
  console.log('Home team name:', homeTeamName);
  console.log('Away team name:', awayTeamName);
  console.log('Match status:', match?.status);
  console.log('Is match completed:', isMatchCompleted());
  console.log('Match sets:', match?.sets);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: isMatchCompleted() 
        ? 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)' 
        : '#FFFFFF',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Enhanced Decorative Background */}
      {!isMatchCompleted() && (
        <>
          <div style={{
            position: 'absolute',
            top: '-5%',
            right: '-5%',
            width: '30%',
            height: '30%',
            background: 'radial-gradient(circle, rgba(252, 211, 77, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(40px)'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '10%',
            left: '-5%',
            width: '35%',
            height: '35%',
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: 'blur(40px)'
          }}></div>
        </>
      )}
      {/* Back Button */}
      <motion.button
        onClick={() => navigate('/fan')}
        whileHover={{ scale: 1.05, x: -2 }}
        whileTap={{ scale: 0.95 }}
        style={{
          position: 'fixed',
          top: '1rem',
          left: '1rem',
          zIndex: 999,
          background: 'linear-gradient(135deg, rgba(15, 58, 42, 0.95) 0%, rgba(6, 78, 59, 0.95) 100%)',
          color: 'white',
          border: '2px solid rgba(252, 211, 77, 0.3)',
          borderRadius: '1rem',
          padding: 'clamp(0.625rem, 2vw, 0.875rem) clamp(1rem, 3vw, 1.25rem)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(0.375rem, 1.5vw, 0.625rem)',
          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
          fontWeight: '700',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        <ArrowLeft size={18} />
        <span style={{ display: window.innerWidth < 640 ? 'none' : 'block' }}>
          Back
        </span>
      </motion.button>

      {/* Premium Fullscreen Toggle */}
      <motion.button
        onClick={toggleFullscreen}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 999,
          background: 'linear-gradient(135deg, rgba(15, 58, 42, 0.95) 0%, rgba(6, 78, 59, 0.95) 100%)',
          color: 'white',
          border: '2px solid rgba(252, 211, 77, 0.3)',
          borderRadius: '1rem',
          padding: 'clamp(0.625rem, 2vw, 0.875rem) clamp(1rem, 3vw, 1.25rem)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(0.375rem, 1.5vw, 0.625rem)',
          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
          fontWeight: '700',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
        <span style={{ display: window.innerWidth < 640 ? 'none' : 'block' }}>
          {isFullscreen ? 'Exit' : 'Fullscreen'}
        </span>
      </motion.button>

      {/* Winner Celebration */}
      <AnimatePresence>
        {isMatchCompleted() && winner && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              zIndex: 999,
              padding: 'clamp(1rem, 3vw, 2rem)',
              paddingTop: 'clamp(5rem, 10vw, 8rem)',
              overflowY: 'auto'
            }}
          >
            {/* Back Button inside Winner Celebration */}
            <motion.button
              onClick={() => navigate('/fan')}
              whileHover={{ scale: 1.05, x: -2 }}
              whileTap={{ scale: 0.95 }}
              style={{
                position: 'fixed',
                top: '1rem',
                left: '1rem',
                zIndex: 1000,
                background: 'linear-gradient(135deg, rgba(15, 58, 42, 0.95) 0%, rgba(6, 78, 59, 0.95) 100%)',
                color: 'white',
                border: '2px solid rgba(252, 211, 77, 0.3)',
                borderRadius: '1rem',
                padding: 'clamp(0.625rem, 2vw, 0.875rem) clamp(1rem, 3vw, 1.25rem)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(0.375rem, 1.5vw, 0.625rem)',
                fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                fontWeight: '700',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              <ArrowLeft size={18} />
              <span style={{ display: window.innerWidth < 640 ? 'none' : 'block' }}>
                Back
              </span>
            </motion.button>

            {/* Title */}
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={{
                textAlign: 'center',
                marginBottom: 'clamp(1rem, 3vw, 2rem)',
                width: '100%'
              }}
            >
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(0.5rem, 2vw, 1rem)',
                flexWrap: 'wrap'
              }}>
                <Trophy size={window.innerWidth < 480 ? 32 : window.innerWidth < 768 ? 40 : 60} style={{ color: '#D97706' }} />
                <h1 style={{ 
                  fontSize: 'clamp(1.25rem, 5vw, 3rem)', 
                  fontWeight: 'bold', 
                  color: '#92400E',
                  margin: 0,
                  textShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  whiteSpace: 'nowrap'
                }}>
                  MATCH COMPLETE
                </h1>
                <Trophy size={window.innerWidth < 480 ? 32 : window.innerWidth < 768 ? 40 : 60} style={{ color: '#D97706' }} />
              </div>
            </motion.div>

            {/* Both Teams Display */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              style={{
                width: '100%',
                maxWidth: '900px',
                marginBottom: 'clamp(1.5rem, 4vw, 2.5rem)'
              }}
            >
              <div style={{
                display: 'flex',
                flexDirection: window.innerWidth < 640 ? 'column' : 'row',
                gap: 'clamp(0.75rem, 2vw, 1.5rem)',
                alignItems: 'stretch',
                justifyContent: 'center'
              }}>
                {/* Home Team */}
                <div style={{
                  flex: '1 1 0',
                  background: winner === homeTeamName 
                    ? 'linear-gradient(135deg, #D1FAE5 0%, #10B981 50%, #059669 100%)'
                    : 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '1rem',
                  padding: 'clamp(1rem, 2.5vw, 1.5rem)',
                  textAlign: 'center',
                  border: winner === homeTeamName ? '3px solid #FCD34D' : '2px solid rgba(0,0,0,0.1)',
                  boxShadow: winner === homeTeamName 
                    ? '0 12px 40px rgba(16, 185, 129, 0.5)' 
                    : '0 4px 12px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minWidth: 0
                }}>
                  {winner === homeTeamName && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#FCD34D',
                      color: '#92400E',
                      padding: '0.4rem 1rem',
                      borderRadius: '1.5rem',
                      fontSize: 'clamp(0.7rem, 1.8vw, 0.9rem)',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(252, 211, 77, 0.5)',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}>
                      🏆 WINNER 🏆
                    </div>
                  )}
                  
                  {homeTeamLogo && (
                    <img 
                      src={homeTeamLogo} 
                      alt={homeTeamName}
                      style={{ 
                        width: 'clamp(70px, 18vw, 120px)', 
                        height: 'clamp(70px, 18vw, 120px)', 
                        objectFit: 'cover',
                        borderRadius: '1rem',
                        border: winner === homeTeamName ? '3px solid #FCD34D' : '2px solid #E5E7EB',
                        margin: '0 auto clamp(0.75rem, 2vw, 1rem)',
                        display: 'block',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                      }} 
                      crossOrigin="anonymous"
                    />
                  )}
                  
                  <div style={{
                    fontSize: 'clamp(1rem, 3.5vw, 1.5rem)',
                    fontWeight: 'bold',
                    color: winner === homeTeamName ? 'white' : '#1F2937',
                    marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                    textShadow: winner === homeTeamName ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                    lineHeight: 1.2
                  }}>
                    {homeTeamName}
                  </div>
                  
                  <div style={{
                    fontSize: 'clamp(2.5rem, 10vw, 4rem)',
                    fontWeight: 'bold',
                    color: winner === homeTeamName ? '#FCD34D' : '#9CA3AF',
                    lineHeight: 1
                  }}>
                    {setScore.home}
                  </div>
                </div>

                {/* VS Divider - Desktop Only */}
                {window.innerWidth >= 640 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                    fontWeight: 'bold',
                    color: '#92400E',
                    padding: '0 clamp(0.5rem, 2vw, 1rem)'
                  }}>
                    VS
                  </div>
                )}

                {/* VS Divider - Mobile Only */}
                {window.innerWidth < 640 && (
                  <div style={{
                    textAlign: 'center',
                    fontSize: 'clamp(1.25rem, 4vw, 1.5rem)',
                    fontWeight: 'bold',
                    color: '#92400E',
                    padding: '0.5rem 0'
                  }}>
                    VS
                  </div>
                )}

                {/* Away Team */}
                <div style={{
                  flex: '1 1 0',
                  background: winner === awayTeamName 
                    ? 'linear-gradient(135deg, #DBEAFE 0%, #3B82F6 50%, #2563EB 100%)'
                    : 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '1rem',
                  padding: 'clamp(1rem, 2.5vw, 1.5rem)',
                  textAlign: 'center',
                  border: winner === awayTeamName ? '3px solid #FCD34D' : '2px solid rgba(0,0,0,0.1)',
                  boxShadow: winner === awayTeamName 
                    ? '0 12px 40px rgba(59, 130, 246, 0.5)' 
                    : '0 4px 12px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minWidth: 0
                }}>
                  {winner === awayTeamName && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#FCD34D',
                      color: '#92400E',
                      padding: '0.4rem 1rem',
                      borderRadius: '1.5rem',
                      fontSize: 'clamp(0.7rem, 1.8vw, 0.9rem)',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(252, 211, 77, 0.5)',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}>
                      🏆 WINNER 🏆
                    </div>
                  )}
                  
                  {awayTeamLogo && (
                    <img 
                      src={awayTeamLogo} 
                      alt={awayTeamName}
                      style={{ 
                        width: 'clamp(70px, 18vw, 120px)', 
                        height: 'clamp(70px, 18vw, 120px)', 
                        objectFit: 'cover',
                        borderRadius: '1rem',
                        border: winner === awayTeamName ? '3px solid #FCD34D' : '2px solid #E5E7EB',
                        margin: '0 auto clamp(0.75rem, 2vw, 1rem)',
                        display: 'block',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                      }} 
                      crossOrigin="anonymous"
                    />
                  )}
                  
                  <div style={{
                    fontSize: 'clamp(1rem, 3.5vw, 1.5rem)',
                    fontWeight: 'bold',
                    color: winner === awayTeamName ? 'white' : '#1F2937',
                    marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                    textShadow: winner === awayTeamName ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                    lineHeight: 1.2
                  }}>
                    {awayTeamName}
                  </div>
                  
                  <div style={{
                    fontSize: 'clamp(2.5rem, 10vw, 4rem)',
                    fontWeight: 'bold',
                    color: winner === awayTeamName ? '#FCD34D' : '#9CA3AF',
                    lineHeight: 1
                  }}>
                    {setScore.away}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Set by Set Breakdown */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '1rem',
                padding: 'clamp(1rem, 3vw, 1.5rem)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                width: '100%',
                maxWidth: '700px',
                marginBottom: 'clamp(1rem, 3vw, 1.5rem)'
              }}
            >
              <div style={{
                fontSize: 'clamp(0.875rem, 2.5vw, 1.25rem)',
                fontWeight: 'bold',
                color: '#92400E',
                textAlign: 'center',
                marginBottom: 'clamp(0.75rem, 2vw, 1rem)'
              }}>
                Set Breakdown
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 'clamp(0.5rem, 1.5vw, 0.75rem)'
              }}>
                {match?.sets?.map((set, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                      borderRadius: '0.75rem',
                      padding: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                      textAlign: 'center',
                      border: '2px solid #10B981'
                    }}
                  >
                    <div style={{
                      fontSize: 'clamp(0.65rem, 1.5vw, 0.75rem)',
                      fontWeight: '700',
                      color: '#065F46',
                      marginBottom: '0.25rem'
                    }}>
                      {set.set === 3 ? 'SUPER' : `SET ${set.set}`}
                    </div>
                    <div style={{
                      fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                      fontWeight: 'bold',
                      color: '#059669'
                    }}>
                      {set.homeScore || set.homeGames || 0}-{set.awayScore || set.awayGames || 0}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Player Statistics Summary */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2 }}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '1rem',
                padding: 'clamp(1rem, 3vw, 1.5rem)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                width: '100%',
                maxWidth: '700px',
                marginBottom: 'clamp(1rem, 3vw, 2rem)'
              }}
            >
              <div style={{
                fontSize: 'clamp(0.875rem, 2.5vw, 1.25rem)',
                fontWeight: 'bold',
                color: '#1C563D',
                textAlign: 'center',
                marginBottom: 'clamp(0.75rem, 2vw, 1rem)'
              }}>
                Player Statistics
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth < 640 ? '1fr' : '1fr 1fr',
                gap: 'clamp(0.75rem, 2vw, 1rem)'
              }}>
                {/* Home Team Players */}
                <div>
                  <div style={{
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                    fontWeight: 'bold',
                    color: '#10B981',
                    textAlign: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    {homeTeamName}
                  </div>
                  {playerStats.home.map((player, index) => (
                    <div
                      key={index}
                      style={{
                        background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                        borderRadius: '0.75rem',
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        border: '2px solid #10B981',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <div style={{
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                        fontWeight: 'bold',
                        color: '#065F46',
                        marginBottom: '0.5rem',
                        textAlign: 'center'
                      }}>
                        {player.name || `Player ${index + 1}`}
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '0.5rem',
                        fontSize: 'clamp(0.7rem, 1.5vw, 0.75rem)'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#10B981', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.aces || 0}
                          </div>
                          <div style={{ color: '#065F46' }}>Aces</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#EF4444', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.unforcedErrors || 0}
                          </div>
                          <div style={{ color: '#DC2626' }}>Errors</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#3B82F6', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.kickSmashes || 0}
                          </div>
                          <div style={{ color: '#1E40AF' }}>Smashes</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#F59E0B', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.sportsmanshipRating ? player.sportsmanshipRating.toFixed(1) : (player.akhlaaqRating ? player.akhlaaqRating.toFixed(1) : 'N/A')}
                          </div>
                          <div style={{ color: '#92400E' }}>Sports.</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Away Team Players */}
                <div>
                  <div style={{
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                    fontWeight: 'bold',
                    color: '#3B82F6',
                    textAlign: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    {awayTeamName}
                  </div>
                  {playerStats.away.map((player, index) => (
                    <div
                      key={index}
                      style={{
                        background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                        borderRadius: '0.75rem',
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        border: '2px solid #3B82F6',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <div style={{
                        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                        fontWeight: 'bold',
                        color: '#1E40AF',
                        marginBottom: '0.5rem',
                        textAlign: 'center'
                      }}>
                        {player.name || `Player ${index + 1}`}
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '0.5rem',
                        fontSize: 'clamp(0.7rem, 1.5vw, 0.75rem)'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#10B981', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.aces || 0}
                          </div>
                          <div style={{ color: '#065F46' }}>Aces</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#EF4444', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.unforcedErrors || 0}
                          </div>
                          <div style={{ color: '#DC2626' }}>Errors</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#3B82F6', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.kickSmashes || 0}
                          </div>
                          <div style={{ color: '#1E40AF' }}>Smashes</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: '#F59E0B', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                            {player.sportsmanshipRating ? player.sportsmanshipRating.toFixed(1) : (player.akhlaaqRating ? player.akhlaaqRating.toFixed(1) : 'N/A')}
                          </div>
                          <div style={{ color: '#92400E' }}>Sports.</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Scoreboard */}
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        padding: 'clamp(1rem, 3vw, 2rem)',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Premium Branding Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            textAlign: 'center',
            marginBottom: 'clamp(1rem, 3vw, 2rem)'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(0.75rem, 2vw, 1.5rem)',
            marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
            flexWrap: 'wrap'
          }}>
            <img 
              src="/offcourt_logo.png" 
              alt="OffCourt" 
              style={{ 
                height: 'clamp(35px, 7vw, 50px)',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
              }} 
            />
            <div style={{
              height: 'clamp(25px, 5vw, 35px)',
              width: '1.5px',
              background: 'linear-gradient(180deg, transparent, rgba(252, 211, 77, 0.5), transparent)'
            }}></div>
            <img 
              src="/thinkincode-emblem.png" 
              alt="ThinkinCode" 
              style={{ 
                height: 'clamp(30px, 6vw, 40px)',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
              }} 
            />
          </div>
        </motion.div>

        {/* Match Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            textAlign: 'center',
            marginBottom: 'clamp(1.5rem, 4vw, 3rem)'
          }}
        >
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 'clamp(1rem, 3vw, 2rem)',
            marginBottom: 'clamp(1rem, 3vw, 1.5rem)'
          }}>
            {/* Home Team Logo */}
            <motion.div 
              whileHover={{ scale: 1.05, rotate: 5 }}
              style={{ 
                width: 'clamp(60px, 15vw, 100px)', 
                height: 'clamp(60px, 15vw, 100px)', 
                borderRadius: '1.25rem',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)',
                border: 'clamp(2px, 0.75vw, 4px) solid white',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {homeTeamLogo ? (
                <img 
                  src={homeTeamLogo} 
                  alt={`${homeTeamName} logo`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    console.log('Home team logo failed to load:', homeTeamLogo);
                    e.target.style.display = 'none';
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div style={{ 
                width: '100%', 
                height: '100%', 
                display: homeTeamLogo ? 'none' : 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'white',
                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                fontWeight: '900'
              }}>
                {homeTeamName ? homeTeamName.charAt(0).toUpperCase() : <Shield size={window.innerWidth < 640 ? 32 : 48} style={{ color: 'white' }} />}
              </div>
            </motion.div>
            
            <div style={{ textAlign: 'center' }}>
              <h1 style={{ 
                fontSize: 'clamp(1.25rem, 5vw, 2.5rem)', 
                fontWeight: '900', 
                color: '#1F2937',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
                marginBottom: 'clamp(0.5rem, 2vw, 0.75rem)'
              }}>
                {homeTeamName} <span style={{ 
                  background: 'linear-gradient(135deg, #0F3A2A 0%, #10B981 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: 'clamp(1rem, 4vw, 2rem)',
                  fontWeight: '900'
                }}>vs</span> {awayTeamName}
              </h1>
            </div>
            
            {/* Away Team Logo */}
            <motion.div 
              whileHover={{ scale: 1.05, rotate: -5 }}
              style={{ 
                width: 'clamp(60px, 15vw, 100px)', 
                height: 'clamp(60px, 15vw, 100px)', 
                borderRadius: '1.25rem',
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 40px rgba(59, 130, 246, 0.4)',
                border: 'clamp(2px, 0.75vw, 4px) solid white',
                overflow: 'hidden'
              }}
            >
              {awayTeamLogo ? (
                <img 
                  src={awayTeamLogo} 
                  alt={`${awayTeamName} logo`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    console.log('Away team logo failed to load:', awayTeamLogo);
                    e.target.style.display = 'none';
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div style={{ 
                width: '100%', 
                height: '100%', 
                display: awayTeamLogo ? 'none' : 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'white',
                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                fontWeight: '900'
              }}>
                {awayTeamName ? awayTeamName.charAt(0).toUpperCase() : <Shield size={window.innerWidth < 640 ? 32 : 48} style={{ color: 'white' }} />}
              </div>
            </motion.div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 'clamp(0.5rem, 2vw, 1rem)',
            fontSize: 'clamp(0.75rem, 2vw, 0.95rem)',
            fontWeight: '700',
            flexWrap: 'wrap'
          }}>
            {/* Enhanced Status Badge */}
            <motion.div 
              animate={{ 
                boxShadow: match?.status === 'live' 
                  ? ['0 0 0 0 rgba(16, 185, 129, 0)', '0 0 0 10px rgba(16, 185, 129, 0)']
                  : 'none'
              }}
              transition={{ duration: 1, repeat: match?.status === 'live' ? Infinity : 0 }}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: match?.status === 'live' 
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : match?.status === 'paused'
                  ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
                  : 'linear-gradient(135deg, rgba(107, 114, 128, 0.8) 0%, rgba(75, 85, 99, 0.8) 100%)',
                padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)',
                borderRadius: '2rem',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              {match?.status === 'live' ? (
                <>
                  <motion.div
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Flame size={18} />
                  </motion.div>
                  <span>LIVE</span>
                </>
              ) : match?.status === 'paused' ? (
                <>
                  <Pause size={18} />
                  <span>PAUSED</span>
                </>
              ) : (
                <>
                  <Clock size={18} />
                  <span>SCHEDULED</span>
                </>
              )}
            </motion.div>
            
            {/* Court Badge */}
            {match?.court && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.2) 0%, rgba(251, 191, 36, 0.2) 100%)',
                padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)',
                borderRadius: '2rem',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(252, 211, 77, 0.3)',
                color: '#FCD34D',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                <Flag size={16} />
                <span>{match.court}</span>
              </div>
            )}
            
            {/* Match Number */}
            {match?.matchNumber && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)',
                padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)',
                borderRadius: '2rem',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(59, 130, 246, 0.3)',
                color: '#60A5FA',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                <Target size={16} />
                <span>#{match.matchNumber}</span>
              </div>
            )}
            
            {/* Set Indicator */}
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
              padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)',
              borderRadius: '2rem',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: '900',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Set {match?.currentSet || 1} of 3
            </div>
          </div>
        </motion.div>

        {/* Player Pairings Display */}
        {match?.selectedPlayers && (() => {
          // Debug logging
          console.log('Pairings Display - selectedPlayers:', match.selectedPlayers);
          console.log('Home player1:', match.selectedPlayers.home?.player1);
          console.log('Home player2:', match.selectedPlayers.home?.player2);
          console.log('Away player1:', match.selectedPlayers.away?.player1);
          console.log('Away player2:', match.selectedPlayers.away?.player2);
          
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '1.5rem',
                padding: 'clamp(1rem, 3vw, 1.5rem)',
                marginBottom: '1.5rem',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(20px)'
              }}
            >
              <div style={{
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                fontWeight: '700',
                color: '#6B7280',
                textAlign: 'center',
                marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Match Pairings
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'clamp(1rem, 3vw, 1.5rem)'
              }}>
                {/* Home Team Pairing */}
                <div style={{
                  textAlign: 'center',
                  padding: 'clamp(0.75rem, 2vw, 1rem)',
                  background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                  borderRadius: '1rem',
                  border: '2px solid #10B981'
                }}>
                  <div style={{
                    fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                    color: '#065F46',
                    marginBottom: '0.5rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    {homeTeamLogo && (
                      <img
                        src={homeTeamLogo}
                        alt={homeTeamName}
                        style={{
                          width: 'clamp(20px, 4vw, 24px)',
                          height: 'clamp(20px, 4vw, 24px)',
                          borderRadius: '0.375rem',
                          objectFit: 'cover'
                        }}
                        crossOrigin="anonymous"
                      />
                    )}
                    {homeTeamName}
                  </div>
                  <div style={{
                    fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
                    fontWeight: '600',
                    color: '#1F2937'
                  }}>
                    {(match.selectedPlayers.home?.player1?.name || 'Player 1')} {' & '} {(match.selectedPlayers.home?.player2?.name || 'Player 2')}
                  </div>
                </div>

                {/* VS Divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                  fontWeight: 'bold',
                  color: '#9CA3AF',
                  gridColumn: window.innerWidth < 500 ? '1 / -1' : 'auto'
                }}>
                  VS
                </div>

                {/* Away Team Pairing */}
                <div style={{
                  textAlign: 'center',
                  padding: 'clamp(0.75rem, 2vw, 1rem)',
                  background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                  borderRadius: '1rem',
                  border: '2px solid #3B82F6'
                }}>
                  <div style={{
                    fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                    color: '#1E40AF',
                    marginBottom: '0.5rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    {awayTeamLogo && (
                      <img
                        src={awayTeamLogo}
                        alt={awayTeamName}
                        style={{
                          width: 'clamp(20px, 4vw, 24px)',
                          height: 'clamp(20px, 4vw, 24px)',
                          borderRadius: '0.375rem',
                          objectFit: 'cover'
                        }}
                        crossOrigin="anonymous"
                      />
                    )}
                    {awayTeamName}
                  </div>
                  <div style={{
                    fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
                    fontWeight: '600',
                    color: '#1F2937'
                  }}>
                    {(match.selectedPlayers.away?.player1?.name || 'Player 1')} {' & '} {(match.selectedPlayers.away?.player2?.name || 'Player 2')}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Main Score Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          style={{
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
            borderRadius: '2rem',
            padding: 'clamp(1.5rem, 4vw, 3rem)',
            marginBottom: '2rem',
            boxShadow: '0 25px 70px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative corner accents */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%)',
            borderRadius: '0 0 100% 0'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, transparent 100%)',
            borderRadius: '100% 0 0 0'
          }}></div>
          {/* Set Score */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: 'clamp(1.5rem, 4vw, 3rem)' 
          }}>
            <div style={{ 
              fontSize: 'clamp(1rem, 3vw, 1.5rem)', 
              fontWeight: 'bold', 
              color: '#6B7280',
              marginBottom: 'clamp(0.5rem, 2vw, 1rem)'
            }}>
              SET SCORE
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: 'clamp(1rem, 3vw, 2rem)',
              flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                {homeTeamLogo && (
                  <img 
                    src={homeTeamLogo} 
                    alt={homeTeamName}
                    style={{ 
                      width: 'clamp(50px, 12vw, 100px)', 
                      height: 'clamp(50px, 12vw, 100px)', 
                      objectFit: 'cover',
                      borderRadius: 'clamp(0.75rem, 2vw, 1.5rem)',
                      border: 'clamp(2px, 0.5vw, 4px) solid #10B981',
                      margin: '0 auto clamp(0.5rem, 2vw, 1rem)',
                      display: 'block',
                      boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)'
                    }} 
                    crossOrigin="anonymous"
                  />
                )}
                <div style={{ 
                  fontSize: 'clamp(4rem, 12vw, 8rem)', 
                  fontWeight: 'bold', 
                  color: '#10B981',
                  lineHeight: 1,
                  textShadow: '0 4px 8px rgba(16, 185, 129, 0.3)'
                }}>
                  {setScore.home}
                </div>
                <div style={{ 
                  fontSize: 'clamp(1rem, 3vw, 1.5rem)', 
                  fontWeight: '700', 
                  color: '#1C563D',
                  marginTop: 'clamp(0.25rem, 1vw, 0.5rem)'
                }}>
                  {homeTeamName}
                </div>
              </div>
              <div style={{ 
                fontSize: 'clamp(3rem, 8vw, 6rem)', 
                color: '#9CA3AF',
                fontWeight: 'bold'
              }}>
                -
              </div>
              <div style={{ textAlign: 'center' }}>
                {awayTeamLogo && (
                  <img 
                    src={awayTeamLogo} 
                    alt={awayTeamName}
                    style={{ 
                      width: 'clamp(50px, 12vw, 100px)', 
                      height: 'clamp(50px, 12vw, 100px)', 
                      objectFit: 'cover',
                      borderRadius: 'clamp(0.75rem, 2vw, 1.5rem)',
                      border: 'clamp(2px, 0.5vw, 4px) solid #3B82F6',
                      margin: '0 auto clamp(0.5rem, 2vw, 1rem)',
                      display: 'block',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
                    }} 
                    crossOrigin="anonymous"
                  />
                )}
                <div style={{ 
                  fontSize: 'clamp(4rem, 12vw, 8rem)', 
                  fontWeight: 'bold', 
                  color: '#3B82F6',
                  lineHeight: 1,
                  textShadow: '0 4px 8px rgba(59, 130, 246, 0.3)'
                }}>
                  {setScore.away}
                </div>
                <div style={{ 
                  fontSize: 'clamp(1rem, 3vw, 1.5rem)', 
                  fontWeight: '700', 
                  color: '#1C563D',
                  marginTop: 'clamp(0.25rem, 1vw, 0.5rem)'
                }}>
                  {awayTeamName}
                </div>
              </div>
            </div>
          </div>

          {/* Individual Set Scores */}
          {match?.sets && match.sets.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{
                marginTop: 'clamp(1.5rem, 4vw, 2.5rem)',
                padding: 'clamp(1rem, 3vw, 1.5rem)',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
                borderRadius: '1rem',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}
            >
              <div style={{
                fontSize: 'clamp(0.875rem, 2.5vw, 1.125rem)',
                fontWeight: '700',
                color: '#1C563D',
                marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Set Breakdown
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 'clamp(0.5rem, 2vw, 1rem)',
                flexWrap: 'wrap'
              }}>
                {match.sets.map((set, index) => {
                  const isCurrentSet = match.currentSet === index + 1;
                  const isCompleted = set.completed;
                  const homeScore = set.homeScore || set.homeGames || 0;
                  const awayScore = set.awayScore || set.awayGames || 0;
                  
                  return (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      style={{
                        background: isCurrentSet 
                          ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                          : isCompleted 
                            ? 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)'
                            : 'linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)',
                        color: isCurrentSet || isCompleted ? 'white' : '#374151',
                        padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(0.75rem, 3vw, 1rem)',
                        borderRadius: '0.75rem',
                        textAlign: 'center',
                        minWidth: 'clamp(60px, 15vw, 80px)',
                        border: isCurrentSet ? '2px solid #10B981' : '2px solid transparent',
                        boxShadow: isCurrentSet 
                          ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                          : '0 2px 4px rgba(0, 0, 0, 0.1)',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        fontSize: 'clamp(0.625rem, 2vw, 0.75rem)',
                        fontWeight: '600',
                        marginBottom: '0.25rem',
                        opacity: 0.8
                      }}>
                        Set {index + 1}
                        {isCurrentSet && ' ▶'}
                        {isCompleted && ' ✓'}
                      </div>
                      <div style={{
                        fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                        fontWeight: '800',
                        lineHeight: 1
                      }}>
                        {homeScore} - {awayScore}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Current Game Score */}
          {!isMatchCompleted() && (
            <div style={{ 
              textAlign: 'center',
              padding: 'clamp(1rem, 3vw, 2rem)',
              background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
              borderRadius: '1.5rem',
              border: '2px solid #10B981'
            }}>
              <div style={{ 
                fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', 
                fontWeight: 'bold', 
                color: '#065F46',
                marginBottom: 'clamp(0.5rem, 1.5vw, 1rem)'
              }}>
                CURRENT GAME
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 'clamp(1rem, 3vw, 2rem)',
                flexWrap: 'wrap'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: 'clamp(2.5rem, 8vw, 4rem)', 
                    fontWeight: 'bold', 
                    color: '#10B981'
                  }}>
                    {gameScore.home}
                  </div>
                  <div style={{ 
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)', 
                    fontWeight: '600', 
                    color: '#065F46'
                  }}>
                    {homeTeamName}
                  </div>
                </div>
                <div style={{ 
                  fontSize: 'clamp(2rem, 6vw, 3rem)', 
                  color: '#9CA3AF',
                  fontWeight: 'bold'
                }}>
                  -
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: 'clamp(2.5rem, 8vw, 4rem)', 
                    fontWeight: 'bold', 
                    color: '#3B82F6'
                  }}>
                    {gameScore.away}
                  </div>
                  <div style={{ 
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)', 
                    fontWeight: '600', 
                    color: '#1E40AF'
                  }}>
                    {awayTeamName}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Point Score */}
          {!isMatchCompleted() && (
            <div style={{ 
              textAlign: 'center',
              padding: 'clamp(1rem, 2.5vw, 1.5rem)',
              background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
              borderRadius: '1rem',
              border: '2px solid #3B82F6',
              marginTop: '1rem'
            }}>
              <div style={{ 
                fontSize: 'clamp(0.875rem, 2vw, 1rem)', 
                fontWeight: 'bold', 
                color: '#1E40AF',
                marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)'
              }}>
                CURRENT POINTS
              </div>
              <div style={{ 
                fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', 
                fontWeight: 'bold', 
                color: '#1C563D'
              }}>
                {getCurrentPointScore()}
              </div>
            </div>
          )}
        </motion.div>

        {/* Player Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '2rem',
            padding: 'clamp(1rem, 3vw, 2rem)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(20px)'
          }}
        >
          <h3 style={{ 
            fontSize: 'clamp(1.25rem, 4vw, 2rem)', 
            fontWeight: 'bold', 
            color: '#1C563D', 
            textAlign: 'center',
            marginBottom: 'clamp(1rem, 3vw, 2rem)'
          }}>
            Player Statistics
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: 'clamp(1rem, 3vw, 2rem)' 
          }}>
            {/* Home Team Stats */}
            <div>
              <h4 style={{ 
                fontSize: 'clamp(1.125rem, 3.5vw, 1.5rem)', 
                fontWeight: 'bold', 
                color: '#10B981', 
                textAlign: 'center',
                marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(0.5rem, 1.5vw, 0.75rem)'
              }}>
                {homeTeamLogo ? (
                  <img 
                    src={homeTeamLogo} 
                    alt={homeTeamName}
                    style={{ 
                      width: 'clamp(40px, 8vw, 50px)', 
                      height: 'clamp(40px, 8vw, 50px)', 
                      borderRadius: '0.75rem',
                      objectFit: 'cover',
                      border: '2px solid #10B981',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }} 
                    crossOrigin="anonymous"
                  />
                ) : (
                  <Target size={window.innerWidth < 640 ? 18 : 24} />
                )}
                {homeTeamName}
              </h4>
              
              <div style={{ display: 'grid', gap: '1rem' }}>
                {playerStats.home.map((player, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                      borderRadius: '1rem',
                      padding: 'clamp(1rem, 2vw, 1.5rem)',
                      border: '2px solid #10B981'
                    }}
                  >
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                      color: '#065F46',
                      marginBottom: '1rem',
                      textAlign: 'center'
                    }}>
                      {player.name || `Player ${index + 1}`}
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: 'clamp(0.75rem, 2vw, 1rem)' 
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#10B981' 
                        }}>
                          {player.aces || 0}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#065F46',
                          fontWeight: '600'
                        }}>
                          Aces
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#EF4444' 
                        }}>
                          {player.unforcedErrors || 0}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#DC2626',
                          fontWeight: '600'
                        }}>
                          Errors
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#3B82F6' 
                        }}>
                          {player.kickSmashes || 0}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#1E40AF',
                          fontWeight: '600'
                        }}>
                          Smashes
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#F59E0B' 
                        }}>
                          {player.sportsmanshipRating ? player.sportsmanshipRating.toFixed(1) : (player.akhlaaqRating ? player.akhlaaqRating.toFixed(1) : 'N/A')}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#92400E',
                          fontWeight: '600'
                        }}>
                          Sportsmanship
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Away Team Stats */}
            <div>
              <h4 style={{ 
                fontSize: 'clamp(1.125rem, 3.5vw, 1.5rem)', 
                fontWeight: 'bold', 
                color: '#3B82F6', 
                textAlign: 'center',
                marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(0.5rem, 1.5vw, 0.75rem)'
              }}>
                {awayTeamLogo ? (
                  <img 
                    src={awayTeamLogo} 
                    alt={awayTeamName}
                    style={{ 
                      width: 'clamp(40px, 8vw, 50px)', 
                      height: 'clamp(40px, 8vw, 50px)', 
                      borderRadius: '0.75rem',
                      objectFit: 'cover',
                      border: '2px solid #3B82F6',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }} 
                    crossOrigin="anonymous"
                  />
                ) : (
                  <Zap size={window.innerWidth < 640 ? 18 : 24} />
                )}
                {awayTeamName}
              </h4>
              
              <div style={{ display: 'grid', gap: '1rem' }}>
                {playerStats.away.map((player, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                      borderRadius: '1rem',
                      padding: 'clamp(1rem, 2vw, 1.5rem)',
                      border: '2px solid #3B82F6'
                    }}
                  >
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                      color: '#1E40AF',
                      marginBottom: '1rem',
                      textAlign: 'center'
                    }}>
                      {player.name || `Player ${index + 1}`}
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: 'clamp(0.75rem, 2vw, 1rem)' 
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#10B981' 
                        }}>
                          {player.aces || 0}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#065F46',
                          fontWeight: '600'
                        }}>
                          Aces
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#EF4444' 
                        }}>
                          {player.unforcedErrors || 0}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#DC2626',
                          fontWeight: '600'
                        }}>
                          Errors
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#3B82F6' 
                        }}>
                          {player.kickSmashes || 0}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#1E40AF',
                          fontWeight: '600'
                        }}>
                          Smashes
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
                          fontWeight: 'bold', 
                          color: '#F59E0B' 
                        }}>
                          {player.sportsmanshipRating ? player.sportsmanshipRating.toFixed(1) : (player.akhlaaqRating ? player.akhlaaqRating.toFixed(1) : 'N/A')}
                        </div>
                        <div style={{ 
                          fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)', 
                          color: '#92400E',
                          fontWeight: '600'
                        }}>
                          Sportsmanship
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FanViewPage;